import { useCallback, useState } from 'react';
import { RowPeekDrawer } from '../components/shared/RowPeekDrawer';
import { RowPeekNavLink } from '../components/shared/RowPeekNavLink';
import { formatNumber, formatRatio, getStatus } from '../lib/utils';
import { useMetroData } from '../hooks/useMetroData';
import { useFilteredMetros } from '../hooks/useFilteredMetros';
import { RankingsTableView } from '../components/rankings/RankingsTableView';
import { RankingsFilterBar } from '../components/rankings/RankingsFilterBar';
import { NationalReferenceStat } from '../components/rankings/NationalReferenceStat';
import { LoadingState } from '../components/shared/LoadingState';
import { ErrorState } from '../components/shared/ErrorState';
import { TableSourceNote } from '../components/shared/TableSourceNote';
// 2026-07-02 (Georgetown feedback): first-visit IntroModal removed
// across all data pages. See MapExplorer.tsx for the note.
import { SectionNav } from '../components/layout/SectionNav';
import { CewSiteFooter } from '../components/layout/CewSiteFooter';
import { useFilterStore } from '../stores/filterStore';
import { useRankingMetrics } from '../hooks/useRankingMetrics';
import { lookupSectorByChipId } from '../lib/occupation-utils';
import { toCsv, type CsvColumn } from '../lib/csv';
import type { Metro } from '../types/metro';
import type { RankingMetric } from '../hooks/useRankingMetrics';

/**
 * Rankings page shell — matches `_archive/rankings.html` layout with one
 * structural improvement (Luis 2026-05-17): the filter bar now lives in
 * the natural page flow below the page heading instead of being a fixed
 * overlay at the top. It uses `position: sticky` so it only pins to the
 * top (below the 56px Topbar + 60px SectionNav) once the user scrolls
 * past it — at which point an elevation shadow lights up, mirroring the
 * SectionNav's `is-stuck` treatment.
 *
 * Layout order:
 *   <Topbar>             — sticky top-0 (rendered higher in app shell)
 *   <SectionNav>         — sticky top-[var(--h-topbar)] (h-60px)
 *   <header>             — page heading + subtitle, max-w-1200
 *   <RankingsFilterBar>  — sticky top-[var(--offset-nav-stack)], full-width bg + shadow,
 *                          inner controls aligned to max-w-1200 grid
 *   <section>            — National stat + methodology + table
 *   <CewSiteFooter>
 *
 * The page section no longer needs the huge `pt-[200px]` offset because
 * the filter bar is now part of the document flow instead of floating
 * above it.
 */
interface CsvRow {
  metro: Metro;
  metric: RankingMetric;
}

/**
 * CSV columns mirror the visible table — sentence-case labels per
 * 2026-05-18 feedback. Default columns (no occupational group
 * selected) export springboard count / shortage share / ratio /
 * most-underproduced group. Lens columns swap to number of
 * credentials / number of jobs / ratio / credential shortage.
 */
// 2026-07-02 (V2 feedback): CSV column headers rewritten to match the
// on-screen column labels exactly, per the recurring column-name spec.
// The download should read as a mirror of what the user is looking at.
const DEFAULT_CSV_COLUMNS: ReadonlyArray<CsvColumn<CsvRow>> = [
  { label: 'Metro area', value: ({ metro }) => metro.name },
  { label: 'Springboard occupational group', value: ({ metric }) => metric.springboardCount },
  {
    label: 'Share of occupational groups with shortages',
    value: ({ metric }) => `${Math.round(metric.shortageShare * 100)}%`,
  },
  {
    label: 'Credentials-to-jobs ratio',
    value: ({ metro }) => metro.ratio?.toFixed(2) ?? '',
  },
  {
    label: 'Occupational group with largest shortage',
    value: ({ metric }) => metric.mostUnderproducedGroup,
  },
];

const LENS_CSV_COLUMNS: ReadonlyArray<CsvColumn<CsvRow>> = [
  { label: 'Metro area', value: ({ metro }) => metro.name },
  { label: 'Annual credentials awarded', value: ({ metric }) => metric.lensCredentials ?? '' },
  { label: 'Annual job openings', value: ({ metric }) => metric.lensJobs ?? '' },
  {
    label: 'Credentials-to-jobs ratio',
    value: ({ metric }) => (metric.lensRatio ?? 0).toFixed(2),
  },
  {
    label: 'Annual credential shortage',
    // CR-013 + CR-017: the CSV must MIRROR the on-screen lens cell
    // (RankingsRow -> shortageCell). Read Georgetown's supplied lensShortage
    // straight through — NOT the old max(0, jobs - credentials)
    // reconstruction — and key the em-dash off the DEC-056 status: surplus
    // -> '—', else the (truncated, possibly-0) shortage. Raw number (no
    // formatNumber) keeps the CSV field comma-free.
    value: ({ metric }) => {
      const status = metric.lensStatus ?? getStatus(metric.lensRatio ?? 0);
      if (status === 'surplus') return '—';
      return metric.lensShortage ?? '';
    },
  },
];

export default function RankingsTable() {
  const { metros, isLoading, error, retry } = useMetroData();
  // Row-peek drawer state. `peekedMetro` is the metro the user clicked;
  // null means the drawer is closed. We open by setting + close by
  // null-ing so the drawer animation can play in both directions
  // (RowPeekDrawer keeps the DOM mounted to animate transit).
  const [peekedMetro, setPeekedMetro] = useState<Metro | null>(null);
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const lensSectorName = lookupSectorByChipId(occupationalGroupId);
  // Same filter pipeline the table renders through, so Copy/Export
  // pull exactly the rows the user is looking at.
  const visibleMetros = useFilteredMetros(metros);
  const metrics = useRankingMetrics(visibleMetros, lensSectorName);
  const getCsvData = useCallback(() => {
    if (visibleMetros.length === 0) return '';
    const rows: ReadonlyArray<CsvRow> = visibleMetros.map((metro) => ({
      metro,
      metric: metrics.get(metro.id) ?? {
        springboardCount: 0,
        shortageShare: 0,
        mostUnderproducedGroup: '—',
      },
    }));
    const columns = lensSectorName ? LENS_CSV_COLUMNS : DEFAULT_CSV_COLUMNS;
    return toCsv(rows, columns);
  }, [visibleMetros, metrics, lensSectorName]);
  return (
    <>
      <SectionNav />
      {/* Page heading sits above the filter bar in natural flow. Once
          the user scrolls down, this heading scrolls out of view and
          the filter bar takes over the top of the viewport. */}
      <div className="mx-auto max-w-page px-8 pt-8">
        <header className="mb-4 flex flex-col gap-2">
          <h1 className="text-[clamp(1.25rem,3vw,1.75rem)] font-bold text-navy">
            Which metros have the biggest credential gaps?
          </h1>
          <p className="text-sm leading-[1.6] text-text-3">
            Sort, filter, and compare annual credential-to-job alignment across 55 US metro areas. Click any metro to explore its full occupational breakdown.
          </p>
        </header>
      </div>
      <RankingsFilterBar getCsvData={getCsvData} />
      <div className="bg-[#FCFCFD]">
        <section className="mx-auto flex max-w-page flex-col gap-4 px-8 pb-12 pt-6">
        {isLoading && <LoadingState label="Loading metros…" />}
        {error && <ErrorState title="Cannot load metros" message={error.message} onRetry={retry} />}
        {!isLoading && !error && (
          <>
            <NationalReferenceStat metros={metros} />
            <RankingsTableView metros={metros} onRowSelect={setPeekedMetro} />
            {/* 2026-07-02 (V2 feedback): source + methodology block
                rendered directly under the table so users can cite the
                page without having to open a separate methodology
                document. Shared component keeps wording identical
                across Rankings, Detail, and Compare. */}
            <TableSourceNote />
          </>
          )}
        </section>
      </div>
      <CewSiteFooter />

      {/* Row-peek -- click any rankings row to surface metro stats +
          cross-tab nav links. Lives outside the table section so it
          overlays the full viewport. Uses the centered `modal`
          variant to match the Providers page (Maria 2026-06-05). */}
      <RowPeekDrawer
        isOpen={peekedMetro !== null}
        onClose={() => setPeekedMetro(null)}
        title={peekedMetro?.name ?? ''}
        eyebrow="METRO AREA"
        variant="modal"
      >
        {peekedMetro && (
          <>
            <dl className="grid grid-cols-2 gap-3 rounded-lg bg-ice/60 p-4 text-sm">
              <div>
                <dt className="text-eyebrow font-semibold uppercase text-text-4">Ratio</dt>
                <dd className="mt-1 font-mono text-base tabular-nums text-text-1">
                  {formatRatio(peekedMetro.ratio)}
                </dd>
              </div>
              <div>
                <dt className="text-eyebrow font-semibold uppercase text-text-4">Status</dt>
                <dd className="mt-1 text-base font-medium capitalize text-text-1">
                  {peekedMetro.status}
                </dd>
              </div>
              <div>
                <dt className="text-eyebrow font-semibold uppercase text-text-4">Credentials</dt>
                <dd className="mt-1 font-mono text-base tabular-nums text-text-1">
                  {formatNumber(peekedMetro.totalCredentials)}
                </dd>
              </div>
              <div>
                <dt className="text-eyebrow font-semibold uppercase text-text-4">Jobs</dt>
                <dd className="mt-1 font-mono text-base tabular-nums text-text-1">
                  {formatNumber(peekedMetro.totalJobs)}
                </dd>
              </div>
            </dl>
            <nav aria-label="Cross-tab navigation" className="flex flex-col gap-1">
              <p className="px-3 pb-1 text-eyebrow font-semibold uppercase text-text-4">
                Explore this metro
              </p>
              <RowPeekNavLink to={`/detail?metroId=${peekedMetro.id}`}>View detail page</RowPeekNavLink>
              <RowPeekNavLink to={`/providers?metroId=${peekedMetro.id}`}>
                Find providers in this metro
              </RowPeekNavLink>
              <RowPeekNavLink to={`/map?metroId=${peekedMetro.id}`}>
                Locate on map
              </RowPeekNavLink>
              <RowPeekNavLink to={`/compare?metroIds=${peekedMetro.id}`}>
                Add to comparison
              </RowPeekNavLink>
            </nav>
          </>
        )}
      </RowPeekDrawer>

    </>
  );
}
